import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Tooltip,
  Grid
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Vehicle } from '../../types/vehicle';
import { reservationService } from '../../services/reservationService';
import {
  format,
  addHours,
  startOfDay,
  addDays,
  startOfWeek,
  endOfWeek,
  differenceInMinutes,
  isSameDay
} from 'date-fns';
import { fr } from 'date-fns/locale';

interface WeekViewProps {
  fleetId: string;
  vehicles: Vehicle[];
  weekStartDate: Date; // Début de la semaine à afficher
  startHour?: number;
  endHour?: number;
}

interface DaySlot {
  date: Date;
  slots: TimeSlot[];
}

interface TimeSlot {
  startTime: Date;
  endTime: Date;
  isOccupied: boolean;
}

const FleetAvailabilityWeekView: React.FC<WeekViewProps> = ({
  fleetId,
  vehicles,
  weekStartDate,
  startHour = 8,
  endHour = 20
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [vehicleDaySlots, setVehicleDaySlots] = useState<Map<string, DaySlot[]>>(new Map());

  // Générer les 7 jours de la semaine
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStartDate, i));

  useEffect(() => {
    loadWeekAvailability();
  }, [fleetId, weekStartDate, vehicles]);

  const loadWeekAvailability = async () => {
    setLoading(true);

    try {
      const weekStart = startOfDay(weekStartDate);
      const weekEnd = addHours(startOfDay(addDays(weekStartDate, 6)), endHour);

      // Récupérer toutes les réservations de la semaine
      const availability = await reservationService.getVehiclesAvailabilityForPeriod(
        fleetId,
        weekStart,
        weekEnd
      );

      // Construire les slots pour chaque véhicule pour chaque jour
      const vehicleSlotsMap = new Map<string, DaySlot[]>();

      vehicles.forEach(vehicle => {
        const vehicleReservations = availability.find(
          a => a.vehicleId === vehicle.id
        )?.reservations || [];

        const daySlots: DaySlot[] = weekDays.map(day => {
          const dayStart = addHours(startOfDay(day), startHour);
          const dayEnd = addHours(startOfDay(day), endHour);

          // Filtrer les réservations pour ce jour
          const dayReservations = vehicleReservations.filter(reservation => {
            const resStart = reservation.startDate;
            const resEnd = reservation.endDate;

            return (
              (resStart >= dayStart && resStart < dayEnd) ||
              (resEnd > dayStart && resEnd <= dayEnd) ||
              (resStart <= dayStart && resEnd >= dayEnd)
            );
          }).sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

          // Construire les slots pour ce jour
          const slots: TimeSlot[] = [];
          let currentTime = new Date(dayStart);

          dayReservations.forEach(reservation => {
            const resStart = reservation.startDate < dayStart ? dayStart : reservation.startDate;
            const resEnd = reservation.endDate > dayEnd ? dayEnd : reservation.endDate;

            // Espace libre avant
            if (currentTime < resStart) {
              slots.push({
                startTime: new Date(currentTime),
                endTime: new Date(resStart),
                isOccupied: false
              });
            }

            // Créneau occupé
            if (resStart < resEnd) {
              slots.push({
                startTime: new Date(resStart),
                endTime: new Date(resEnd),
                isOccupied: true
              });
            }

            currentTime = new Date(resEnd);
          });

          // Espace libre après
          if (currentTime < dayEnd) {
            slots.push({
              startTime: new Date(currentTime),
              endTime: new Date(dayEnd),
              isOccupied: false
            });
          }

          // Si aucune réservation, tout est libre
          if (slots.length === 0) {
            slots.push({
              startTime: new Date(dayStart),
              endTime: new Date(dayEnd),
              isOccupied: false
            });
          }

          return {
            date: day,
            slots
          };
        });

        vehicleSlotsMap.set(vehicle.id, daySlots);
      });

      setVehicleDaySlots(vehicleSlotsMap);
    } catch (error) {
      console.error('Error loading week availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSlotHeight = (slot: TimeSlot, dayStart: Date, dayEnd: Date): number => {
    const totalMinutes = differenceInMinutes(dayEnd, dayStart);
    const slotMinutes = differenceInMinutes(slot.endTime, slot.startTime);
    return (slotMinutes / totalMinutes) * 100;
  };

  const formatDayHeader = (date: Date) => {
    return format(date, 'EEE dd', { locale: fr });
  };

  const formatSlotTooltip = (slot: TimeSlot) => {
    const duration = differenceInMinutes(slot.endTime, slot.startTime);
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    const durationStr = hours > 0
      ? `${hours}h${minutes > 0 ? minutes + 'min' : ''}`
      : `${minutes}min`;

    return `${format(slot.startTime, 'HH:mm', { locale: fr })} - ${format(slot.endTime, 'HH:mm', { locale: fr })} (${durationStr})`;
  };

  const calculateOccupancyRate = (daySlots: DaySlot) => {
    const dayStart = addHours(startOfDay(daySlots.date), startHour);
    const dayEnd = addHours(startOfDay(daySlots.date), endHour);
    const totalMinutes = differenceInMinutes(dayEnd, dayStart);

    const occupiedMinutes = daySlots.slots
      .filter(slot => slot.isOccupied)
      .reduce((sum, slot) => sum + differenceInMinutes(slot.endTime, slot.startTime), 0);

    return Math.round((occupiedMinutes / totalMinutes) * 100);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isToday = (date: Date) => isSameDay(date, new Date());

  return (
    <Box>
      {/* En-tête avec légende */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, minWidth: 300 }}>
          {t('reservation:calendar.legend', 'Affichage Occupé/Libre uniquement (sans identité du réservant)')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            size="small"
            sx={{
              bgcolor: '#2e7d32',
              color: 'white',
              fontWeight: 500,
              '& .MuiChip-label': { px: 1.5 }
            }}
            label={t('reservation:calendar.free', 'Libre')}
          />
          <Chip
            size="small"
            sx={{
              bgcolor: '#d32f2f',
              color: 'white',
              fontWeight: 500,
              '& .MuiChip-label': { px: 1.5 }
            }}
            label={t('reservation:calendar.occupied', 'Occupé')}
          />
        </Box>
      </Box>

      {/* Vue semaine */}
      <Paper sx={{ overflow: 'auto' }}>
        <Box sx={{ minWidth: 900 }}>
          {/* En-tête des jours */}
          <Box sx={{ display: 'flex', borderBottom: '2px solid #e0e0e0' }}>
            <Box sx={{ width: 200, p: 2, borderRight: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('reservation:calendar.vehicle', 'Véhicule')}
              </Typography>
            </Box>
            {weekDays.map((day, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  p: 2,
                  borderRight: index < 6 ? '1px solid #e0e0e0' : 'none',
                  bgcolor: isToday(day) ? '#e3f2fd' : 'transparent',
                  textAlign: 'center'
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: isToday(day) ? 700 : 600,
                    color: isToday(day) ? 'primary.main' : 'text.primary'
                  }}
                >
                  {formatDayHeader(day)}
                </Typography>
                {isToday(day) && (
                  <Chip
                    label={t('reservation:calendar.today', "Aujourd'hui")}
                    size="small"
                    color="primary"
                    sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            ))}
          </Box>

          {/* Lignes de véhicules */}
          {vehicles.map((vehicle) => {
            const daySlots = vehicleDaySlots.get(vehicle.id) || [];

            return (
              <Box
                key={vehicle.id}
                sx={{
                  display: 'flex',
                  borderBottom: '1px solid #e0e0e0',
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                {/* Nom du véhicule */}
                <Box
                  sx={{
                    width: 200,
                    p: 2,
                    borderRight: '1px solid #e0e0e0',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {vehicle.make} {vehicle.model}
                  </Typography>
                  {vehicle.licensePlate && (
                    <Typography variant="caption" color="text.secondary">
                      {vehicle.licensePlate}
                    </Typography>
                  )}
                </Box>

                {/* Colonnes des jours */}
                {daySlots.map((daySlot, dayIndex) => {
                  const dayStart = addHours(startOfDay(daySlot.date), startHour);
                  const dayEnd = addHours(startOfDay(daySlot.date), endHour);
                  const occupancyRate = calculateOccupancyRate(daySlot);

                  return (
                    <Box
                      key={dayIndex}
                      sx={{
                        flex: 1,
                        borderRight: dayIndex < 6 ? '1px solid #e0e0e0' : 'none',
                        bgcolor: isToday(daySlot.date) ? '#f9feff' : 'transparent',
                        p: 1
                      }}
                    >
                      {/* Barre verticale de disponibilité */}
                      <Box
                        sx={{
                          height: 120,
                          bgcolor: '#fafafa',
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid #e0e0e0',
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        {daySlot.slots.map((slot, slotIndex) => {
                          const height = getSlotHeight(slot, dayStart, dayEnd);

                          return (
                            <Tooltip
                              key={slotIndex}
                              title={formatSlotTooltip(slot)}
                              arrow
                              placement="top"
                            >
                              <Box
                                sx={{
                                  height: `${height}%`,
                                  bgcolor: slot.isOccupied ? '#d32f2f' : '#2e7d32',
                                  borderBottom: slotIndex < daySlot.slots.length - 1 ? '1px solid white' : 'none',
                                  transition: 'opacity 0.2s',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    opacity: 0.85
                                  }
                                }}
                              />
                            </Tooltip>
                          );
                        })}
                      </Box>

                      {/* Taux d'occupation */}
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          textAlign: 'center',
                          mt: 0.5,
                          color: occupancyRate > 75 ? 'error.main' : occupancyRate > 50 ? 'warning.main' : 'success.main',
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }}
                      >
                        {occupancyRate}%
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            );
          })}

          {vehicles.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {t('reservation:calendar.noVehicles', 'Aucun véhicule à afficher')}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Note de bas de page */}
      <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          <strong>{t('reservation:calendar.timeRange', 'Plage horaire')}:</strong> {startHour}h00 - {endHour}h00
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          💡 {t('reservation:calendar.weekTip', 'Les barres verticales montrent les créneaux de chaque journée. Le % indique le taux d\'occupation.')}
        </Typography>
      </Box>
    </Box>
  );
};

export default FleetAvailabilityWeekView;
